import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import type { Group, Mesh } from "three";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";
import { VoxelSkin } from "./VoxelSkin";

interface BurstProps {
  position: [number, number, number];
  color: string;
  count: number;
}

interface EliminationTransform {
  scale: number;
  lift: number;
  visible: boolean;
}

export function eliminationTransformAt(ageSeconds: number): EliminationTransform {
  const popDuration = YAK_TRAILS_CLIENT_TUNING.effects.eliminationPopSeconds;
  const vanishDuration = YAK_TRAILS_CLIENT_TUNING.effects.eliminationVanishSeconds;
  if (ageSeconds <= popDuration) {
    const progress = Math.max(0, ageSeconds) / popDuration;
    return {
      scale: 1 + Math.sin(progress * Math.PI) * YAK_TRAILS_CLIENT_TUNING.effects.eliminationPopScale,
      lift: progress * YAK_TRAILS_CLIENT_TUNING.effects.eliminationPopLift,
      visible: true,
    };
  }
  const vanishProgress = Math.min(1, (ageSeconds - popDuration) / vanishDuration);
  const eased = vanishProgress * vanishProgress * (3 - 2 * vanishProgress);
  return {
    scale: 1 - eased,
    lift: YAK_TRAILS_CLIENT_TUNING.effects.eliminationPopLift
      + Math.sin(vanishProgress * Math.PI) * YAK_TRAILS_CLIENT_TUNING.effects.eliminationVanishLift,
    visible: vanishProgress < 1,
  };
}

function directions(count: number) {
  return Array.from({ length: count }, (_, index) => {
    const angle = index / count * Math.PI * 2 + (index % 3) * 0.17;
    const speed = YAK_TRAILS_CLIENT_TUNING.effects.burstBaseSpeed
      + (index % 4) * YAK_TRAILS_CLIENT_TUNING.effects.burstSpeedStep;
    return {
      x: Math.cos(angle) * speed,
      z: Math.sin(angle) * speed,
      lift: YAK_TRAILS_CLIENT_TUNING.effects.burstBaseLift
        + (index % 5) * YAK_TRAILS_CLIENT_TUNING.effects.burstLiftStep,
    };
  });
}

export function CaptureSparkles({ position, color, count }: BurstProps) {
  const particles = useRef<Array<Mesh | null>>([]);
  const age = useRef(0);
  const velocities = useMemo(() => directions(count), [count]);
  useFrame((_, delta) => {
    age.current += delta;
    const time = age.current;
    for (let index = 0; index < particles.current.length; index += 1) {
      const particle = particles.current[index];
      const velocity = velocities[index];
      if (!particle || !velocity) continue;
      particle.position.set(
        velocity.x * time,
        velocity.lift * time - YAK_TRAILS_CLIENT_TUNING.effects.sparkleGravity * time * time,
        velocity.z * time,
      );
      particle.rotation.x += delta * 5;
      particle.rotation.y += delta * 7;
      const scale = Math.max(0, 1 - time / YAK_TRAILS_CLIENT_TUNING.effects.sparkleSeconds);
      particle.scale.setScalar(scale);
      particle.visible = time < YAK_TRAILS_CLIENT_TUNING.effects.sparkleSeconds;
    }
  });
  return (
    <group position={position}>
      {velocities.map((_, index) => (
        <mesh key={index} ref={(mesh) => { particles.current[index] = mesh; }} castShadow={false}>
          <octahedronGeometry args={[0.13 + index % 3 * 0.025, 0]} />
          <meshBasicMaterial color={index % 2 === 0 ? color : "#fff7b2"} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

export function DeathCubes({ position, color, count, delay = 0 }: BurstProps & { delay?: number }) {
  const group = useRef<Group>(null);
  const particles = useRef<Array<Mesh | null>>([]);
  const age = useRef(0);
  const velocities = useMemo(() => directions(count).map((direction, index) => ({
    ...direction,
    x: direction.x * YAK_TRAILS_CLIENT_TUNING.effects.deathBurstHorizontalMultiplier,
    z: direction.z * YAK_TRAILS_CLIENT_TUNING.effects.deathBurstHorizontalMultiplier,
    lift: direction.lift
      + YAK_TRAILS_CLIENT_TUNING.effects.deathBurstExtraLift
      + index % 2 * YAK_TRAILS_CLIENT_TUNING.effects.deathBurstAlternatingLift,
  })), [count]);
  useFrame((_, delta) => {
    age.current += delta;
    const time = Math.max(0, age.current - delay);
    if (group.current) {
      group.current.visible = age.current >= delay && time < YAK_TRAILS_CLIENT_TUNING.effects.deathCubeSeconds;
    }
    for (let index = 0; index < particles.current.length; index += 1) {
      const particle = particles.current[index];
      const velocity = velocities[index];
      if (!particle || !velocity) continue;
      particle.position.set(
        velocity.x * time,
        velocity.lift * time - YAK_TRAILS_CLIENT_TUNING.effects.deathCubeGravity * time * time,
        velocity.z * time,
      );
      particle.rotation.x += delta * (5 + index % 4);
      particle.rotation.z += delta * (4 + index % 3);
      const scale = Math.max(0, 1 - time / YAK_TRAILS_CLIENT_TUNING.effects.deathCubeSeconds);
      particle.scale.setScalar(scale);
    }
  });
  return (
    <group ref={group} position={position} visible={delay <= 0}>
      {velocities.map((_, index) => (
        <mesh key={index} ref={(mesh) => { particles.current[index] = mesh; }} castShadow>
          <boxGeometry args={[0.24, 0.24, 0.24]} />
          <meshStandardMaterial color={index % 3 === 0 ? "#ffffff" : color} roughness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function PlayerElimination({
  position,
  color,
  count,
  heading,
  skinId,
}: BurstProps & { heading: number; skinId: string }) {
  const model = useRef<Group>(null);
  const age = useRef(0);
  useFrame((_, delta) => {
    age.current += delta;
    if (!model.current) return;
    const transform = eliminationTransformAt(age.current);
    model.current.visible = transform.visible;
    model.current.position.y = transform.lift;
    model.current.rotation.y += delta * YAK_TRAILS_CLIENT_TUNING.effects.eliminationSpinSpeed;
    model.current.rotation.z = Math.sin(age.current * YAK_TRAILS_CLIENT_TUNING.effects.eliminationWobbleSpeed)
      * YAK_TRAILS_CLIENT_TUNING.effects.eliminationWobbleAmount;
    model.current.scale.setScalar(transform.scale);
  });
  return (
    <group position={position}>
      <group ref={model}>
        <VoxelSkin skinId={skinId} heading={heading} scale={YAK_TRAILS_CLIENT_TUNING.player.remoteScale} />
      </group>
      <DeathCubes
        position={[0, -0.05, 0]}
        color={color}
        count={count}
        delay={YAK_TRAILS_CLIENT_TUNING.effects.deathCubeDelaySeconds}
      />
    </group>
  );
}
